import { lazy, Suspense, useEffect, useState } from 'react';
import { currentEmail, getSession, signIn, signOut } from './auth';
import Status from './pages/Status';
import Diagnostics from './pages/Diagnostics';

const DataAnalysis = lazy(() => import('./pages/DataAnalysis'));

type Tab = 'status' | 'diagnostics' | 'analysis';

function Login({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState<((p: string) => Promise<void>) | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (challenge) {
        await challenge(newPassword);
        onDone();
        return;
      }
      const result = await signIn(email, password);
      if (result.kind === 'newPasswordRequired') {
        // Expected on first sign-in: the pool is admin-create-only, so every
        // account starts with a temporary password.
        setChallenge(() => result.submit);
      } else {
        onDone();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="login" onSubmit={submit}>
      <h1 style={{ fontSize: '1.1rem' }}>Lewis Spa Controller</h1>
      {challenge ? (
        <>
          <p className="msg">Choose a new password to finish setting up this account.</p>
          <input
            type="password" placeholder="New password" autoFocus required
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
          />
        </>
      ) : (
        <>
          <input
            type="email" placeholder="Email" autoComplete="username" autoFocus required
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password" placeholder="Password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </>
      )}
      <button disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
      {error && <p className="err">{error}</p>}
    </form>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('status');

  async function refreshAuth() {
    const session = await getSession();
    setAuthed(Boolean(session));
    setEmail(await currentEmail());
    setReady(true);
  }

  useEffect(() => {
    refreshAuth();
  }, []);

  if (!ready) return null;
  if (!authed) return <Login onDone={refreshAuth} />;

  return (
    <div className={tab === 'analysis' ? 'wrap wide' : 'wrap'}>
      <header className="top">
        <h1>Lewis Spa Controller</h1>
        <span className="who">
          {email}{' '}
          <button
            className="action"
            style={{ padding: '.2rem .55rem', marginLeft: '.5rem' }}
            onClick={() => {
              signOut();
              setAuthed(false);
            }}
          >
            Sign out
          </button>
        </span>
      </header>

      <nav className="tabs">
        <button aria-selected={tab === 'status'} onClick={() => setTab('status')}>
          Status &amp; Control
        </button>
        <button aria-selected={tab === 'diagnostics'} onClick={() => setTab('diagnostics')}>
          Diagnostics
        </button>
        <button aria-selected={tab === 'analysis'} onClick={() => setTab('analysis')}>
          Data Analysis
        </button>
      </nav>

      {tab === 'status' ? (
        <Status />
      ) : tab === 'diagnostics' ? (
        <Diagnostics />
      ) : (
        <Suspense fallback={<p className="sub">Loading…</p>}>
          <DataAnalysis />
        </Suspense>
      )}
    </div>
  );
}
